import { useMemo } from "react";
import { ClasroomsWithDates, DayRowData } from "../context/types";
import { getDateNumber } from "@utils/formats";

interface UseClassroomCoverageProps {
  dateTimeFormatted: DayRowData[];
  selectedClassrooms: ClasroomsWithDates[];
}

const getBookingTimeFormatted = (time: string) => {
  const [hour, minute, second] = time?.split(":");
  return `${getDateNumber(hour || 0)}:${getDateNumber(minute || 0)}:${getDateNumber(second || 0)}`;
};

const useClassroomCoverage = ({
  dateTimeFormatted = [],
  selectedClassrooms = [],
}: UseClassroomCoverageProps) => {
  const adaptedSelectedDates = useMemo(() => {
    return dateTimeFormatted
      ?.map(date => {
        return `${date?.day?.dateValue} ${getBookingTimeFormatted(
          date?.start?.value?.value,
        )} - ${getBookingTimeFormatted(date?.end?.value?.value)}`;
      })
      .join(" | ");
  }, [dateTimeFormatted]);

  const totalAndCoveredDates = useMemo(() => {
    const total = dateTimeFormatted;
    const covered = selectedClassrooms?.flatMap(selectedClassroom => selectedClassroom?.dates);

    const coveredObj = covered?.reduce((acc, date) => {
      acc[
        `${date?.day?.dateValue} ${getBookingTimeFormatted(
          date?.start?.value?.value,
        )} - ${getBookingTimeFormatted(date?.end?.value?.value)}`
      ] = date;
      return acc;
    }, {});

    const missingDatesMap = new Map<string, boolean>();

    const missing = total?.filter(date => {
      const day = date?.day?.dateValue;
      const start = getBookingTimeFormatted(date?.start?.value?.value);
      const end = getBookingTimeFormatted(date?.end?.value?.value);
      const isMissing = !coveredObj[`${day} ${start} - ${end}`];

      if (isMissing) {
        missingDatesMap.set(`${day} ${start} - ${end}`, true);
      }

      return isMissing;
    });

    return {
      total,
      covered,
      missing,
      missingDatesMap,
    };
  }, [selectedClassrooms, dateTimeFormatted]);

  return {
    adaptedSelectedDates,
    totalAndCoveredDates,
  };
};

export default useClassroomCoverage;
